import { Box, Stack } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { BookingData } from "../../../types/view-booking";
import { MemoryTableStats } from "./components/MemoryTableStats";
import apiService from "../../../../../service/Api";
import { RecentCommentsSidebar } from "./components/RecentComments";
import MemoryTypesSidebar from "./components/MemoryTypes";
import { useMemoryMessages } from "./hooks/useMemoryMessages";
import MemoryTableContent from "./components/MemoryTableContent";
import { TopContributorsSidebar } from "./components/TopContributers";
import ContactTypesSidebar from "./components/ContactType";
import BookingsSidebar from "../../components/BookingsSidebar";
import StreamSummaryCard from "./components/StreamSummaryCard";

interface MemoryTableTabProps {
  bookingData: BookingData | null;
}

const MemoryTableTab: React.FC<MemoryTableTabProps> = ({ bookingData }) => {
  const [streamId, setStreamId] = useState<number | null>(null);
  const [streamLink, setStreamLink] = useState<string | undefined>(undefined);
  const [statsRefresh, setStatsRefresh] = useState(0);
  const [activeTab, setActiveTab] = useState(0);

  const {
    messages,
    loading,
    refresh: refreshMessages,
  } = useMemoryMessages(streamId, bookingData?.id);

  const deceasedName = bookingData?.beloved?.name ?? undefined;

  const triggerRefresh = useCallback(() => {
    setStatsRefresh((prev) => prev + 1);
    refreshMessages();
  }, [refreshMessages]);

  const fetchStreamId = useCallback(async () => {
    if (!bookingData?.id) return;
    try {
      const res = await apiService.get<{
        results: { id: number; website_stream_link?: string }[];
      }>(`/api/streams/stream/?booking=${bookingData.id}`);
      const results = res.data?.results;
      if (results?.length > 0) {
        setStreamId(results[0].id);
        setStreamLink(results[0].website_stream_link);
      }
    } catch (err) {
      console.error("Failed to fetch stream:", err);
    }
  }, [bookingData?.id]);

  useEffect(() => {
    fetchStreamId();
  }, [fetchStreamId]);

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <MemoryTableStats bookingId={bookingData?.id} refresh={statsRefresh} />

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 2,
          mt: 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StreamSummaryCard streamId={streamId} />
          <MemoryTableContent
            messages={messages}
            activeTab={activeTab}
            isLoading={loading}
            onStatsChange={triggerRefresh}
            onTabChange={setActiveTab}
            streamId={streamId}
            deceasedName={deceasedName}
            streamLink={streamLink}
          />
        </Box>

        <Stack
          spacing={2}
          sx={{ width: { xs: "100%", lg: 280 }, flexShrink: 0 }}
        >
          {activeTab === 0 && <RecentCommentsSidebar messages={messages} />}
          {activeTab === 1 && <TopContributorsSidebar messages={messages} />}
          {activeTab === 2 && <TopContributorsSidebar messages={messages} />}

          {activeTab === 0 && <MemoryTypesSidebar messages={messages} />}
          {activeTab === 1 && <ContactTypesSidebar messages={messages} />}
          {activeTab === 2 && <BookingsSidebar />}
        </Stack>
      </Box>
    </Box>
  );
};

export default MemoryTableTab;
